import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  type KeyboardEvent,
} from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type VariableOption = {
  name: string;       // e.g. "accounts.name"
  display_name: string; // e.g. "Account Name"
  group?: string;     // e.g. "Account Traits"
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  variables: VariableOption[];
  placeholder?: string;
  rows?: number;
  className?: string;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Parse a string like "Hello {{accounts.name}}, score: {{c__health_score}}" into segments. */
const parseSegments = (text: string): Array<{ type: "text" | "var"; value: string }> => {
  const segments: Array<{ type: "text" | "var"; value: string }> = [];
  const regex = /\{\{([^}]+)\}\}/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: "var", value: match[1].trim() });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }
  return segments;
};

/** Serialize the editor DOM back to a template string. */
const serializeEditor = (el: HTMLDivElement): string => {
  let result = "";
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent || "";
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.dataset.variable) {
        result += `{{${element.dataset.variable}}}`;
      } else if (element.tagName === "BR") {
        result += "\n";
      } else {
        result += element.textContent || "";
      }
    }
  });
  return result;
};

/** Create a chip span for a variable. */
const createChipElement = (variableName: string, displayName: string): HTMLSpanElement => {
  const chip = document.createElement("span");
  chip.contentEditable = "false";
  chip.dataset.variable = variableName;
  chip.className =
    "inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary mx-0.5 align-baseline select-none";
  chip.textContent = displayName;

  const removeBtn = document.createElement("span");
  removeBtn.className = "cursor-pointer ml-0.5 opacity-60 hover:opacity-100";
  removeBtn.textContent = "\u00d7";
  removeBtn.addEventListener("mousedown", (e) => {
    e.preventDefault();
    e.stopPropagation();
    chip.remove();
  });
  chip.appendChild(removeBtn);

  return chip;
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const VariableChipEditor = ({ value, onChange, variables, placeholder, rows = 3, className }: Props) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const initialized = useRef(false);

  /* --- build display name lookup --- */
  const varLookup = useMemo(() => {
    const map: Record<string, string> = {};
    variables.forEach((v) => {
      map[v.name] = v.display_name;
    });
    return map;
  }, [variables]);

  /* --- group variables --- */
  const groupedVars = useMemo(() => {
    const groups: Record<string, VariableOption[]> = {};
    const filtered = search
      ? variables.filter(
          (v) =>
            v.display_name.toLowerCase().includes(search.toLowerCase()) ||
            v.name.toLowerCase().includes(search.toLowerCase()),
        )
      : variables;
    filtered.forEach((v) => {
      const group = v.group || "Other";
      if (!groups[group]) groups[group] = [];
      groups[group].push(v);
    });
    return groups;
  }, [variables, search]);

  /* --- initialize editor content from value --- */
  useEffect(() => {
    if (!editorRef.current || initialized.current) return;
    initialized.current = true;
    const el = editorRef.current;
    el.innerHTML = "";
    const segments = parseSegments(value || "");
    segments.forEach((seg) => {
      if (seg.type === "text") {
        const parts = seg.value.split("\n");
        parts.forEach((part, i) => {
          if (i > 0) el.appendChild(document.createElement("br"));
          if (part) el.appendChild(document.createTextNode(part));
        });
      } else {
        const displayName = varLookup[seg.value] || seg.value;
        el.appendChild(createChipElement(seg.value, displayName));
      }
    });
  }, [value, varLookup]);

  /* --- sync changes back --- */
  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const serialized = serializeEditor(editorRef.current);
    onChange(serialized);
  }, [onChange]);

  /* --- save cursor position before picker opens --- */
  const saveCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
      savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    }
  }, []);

  /* --- insert variable at saved cursor position --- */
  const insertVariable = useCallback(
    (variable: VariableOption) => {
      const el = editorRef.current;
      if (!el) return;

      const chip = createChipElement(variable.name, variable.display_name);
      const space = document.createTextNode("\u00a0");

      // Restore saved cursor position, or append to end
      if (savedRangeRef.current) {
        const range = savedRangeRef.current;
        range.deleteContents();
        range.insertNode(space);
        range.insertNode(chip);

        // Move cursor after space
        const newRange = document.createRange();
        newRange.setStartAfter(space);
        newRange.setEndAfter(space);

        el.focus();
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      } else {
        // No saved position — append at end
        el.appendChild(chip);
        el.appendChild(space);
        el.focus();
      }

      savedRangeRef.current = null;
      handleInput();
      setPickerOpen(false);
      setSearch("");
    },
    [handleInput],
  );

  /* --- prevent enter from creating divs --- */
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      document.execCommand("insertLineBreak");
    }
  };

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          data-placeholder={placeholder || "Type your message..."}
          className={cn(
            "min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none",
          )}
          style={{ minHeight: `${rows * 24}px` }}
        />
      </div>

      <Popover
        open={pickerOpen}
        onOpenChange={(open) => {
          if (open) saveCursorPosition();
          setPickerOpen(open);
          if (!open) setSearch("");
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5">
            <ChevronDown className="h-3 w-3" />
            Insert Variable
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start" side="bottom" sideOffset={4}>
          <div className="border-b p-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fields..."
              className="h-7 text-xs"
              autoFocus
            />
          </div>
          <div className="max-h-[250px] overflow-y-auto p-1">
            {Object.entries(groupedVars).length === 0 && (
              <p className="px-2 py-3 text-center text-xs text-muted-foreground">No variables found</p>
            )}
            {Object.entries(groupedVars).map(([group, vars]) => (
              <div key={group} className="mb-1">
                <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{group}</p>
                {vars.map((v) => (
                  <button
                    key={v.name}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted transition-colors"
                    onClick={() => insertVariable(v)}
                  >
                    <span className="font-medium text-foreground truncate">{v.display_name}</span>
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">{v.name}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default VariableChipEditor;
